<script setup>
import { ref, onMounted } from 'vue'
import { useRequestsStore } from '@/stores/storeRequests'
const storeRequests = useRequestsStore()

// Список размеров для расстановки
const sizes = [
	'small-left',
	'medium-left',
	'large-left',
	'center',
	'large-right',
	'medium-right',
	'small-right',
]

// Маппинг, какие статистики показывать для каждого индекса
const statsMap = [
	[], // 1-й элемент — ничего
	['likes'], // 2-й — лайки
	['likes', 'views'], // 3-й — лайки и просмотры
	[], // 4-й — ничего
	['views_tg', 'views'], // 5-й — поделится и просмотры
	['likes'], // 6-й — лайки
	[], // 7-й — ничего
]

// Нормализация данных с выборочной статистикой
const normalizeData = (data) => {
	return data.map((item, i) => {
		const statsToShow = statsMap[i] ?? []
		const stats = {}
		if (statsToShow.includes('views')) stats.views = item.views ?? item.views_views_tg ?? null
		if (statsToShow.includes('likes')) stats.likes = item.likes ?? null
		if (statsToShow.includes('views_tg')) stats.views_tg = item.views_tg ?? null

		return {
			img: item.image,
			size: sizes[i % sizes.length],
			stats,
		}
	})
}

// Состояние
const items = ref([])

// Форматирование чисел
const formatNumber = (num) => {
	if (num == null) return ''
	if (num >= 1_000_000) return (num / 1_000_000).toFixed(1).replace(/\.0$/, '') + ' млн'
	if (num >= 1_000) return Math.round(num / 1000) + ' тыс'
	return num.toString()
}

onMounted(async () => {
	const data = await storeRequests.getPranks()
	items.value = normalizeData(data)
})
</script>

<template>
	<section class="viral-pranks">
		<div class="viral-pranks__bg">
			<img loading="lazy" src="@/assets/img/viral-pranks/viral-pranks_bg.png" alt="" />
		</div>

		<div class="viral-pranks__container">
			<div class="viral-pranks__title">
				<h2>Пранки и обзоры собирают <span>миллионы</span></h2>
				<p>Без рекламы и договоров</p>
			</div>

			<div class="viral-pranks__wrapper">
				<div class="viral-pranks__list">
					<div
						v-for="(item, i) in items"
						:key="i"
						class="viral-pranks__item"
						:data-size="item.size"
					>
						<div class="viral-pranks__block">
							<div class="viral-pranks__img">
								<img :src="item.img" alt="" />
								<div
									v-if="item.stats.views"
									class="viral-pranks__stat viral-pranks__stat--views"
								>
									<svg width="41" height="25">
										<use href="#view" />
									</svg>
									<span>{{ formatNumber(item.stats.views) }}</span>
								</div>
								<div
									v-if="item.stats.likes"
									class="viral-pranks__stat viral-pranks__stat--likes"
								>
									<svg width="33" height="32">
										<use href="#like" />
									</svg>
									<span>{{ formatNumber(item.stats.likes) }}</span>
								</div>
								<div
									v-if="item.stats.views_tg"
									class="viral-pranks__stat viral-pranks__stat--views_tg"
								>
									<svg width="40" height="36">
										<use href="#repost" />
									</svg>
									<span>{{ formatNumber(item.stats.views_tg) }}</span>
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>
		</div>
	</section>
</template>

<style lang="scss" scoped>
.viral-pranks {
	padding: 7.6rem 0 13.2rem;
	position: relative;

	@media (max-width: 991.98px) {
		padding: 2.9rem 0 3.9rem;
	}

	/* ---------- Background ---------- */
	&__bg {
		position: absolute;
		top: 5rem;
		left: 0;
		width: 100%;
		height: 100%;
		z-index: -1;
		pointer-events: none;
		user-select: none;

		@media (max-width: 991.98px) {
			top: 7rem;
			height: 70%;
		}

		img {
			width: 100%;
			height: 100%;
			object-fit: cover;
		}
	}

	/* ---------- Title ---------- */
	&__title {
		text-align: center;

		p {
			margin-top: 2.6rem;

			@media (max-width: 991.98px) {
				margin-top: 1.7rem;
			}
		}
	}

	/* ---------- Wrapper ---------- */
	&__wrapper {
		margin-top: 5.2rem;
		display: flex;
		justify-content: center;

		@media (max-width: 991.98px) {
			margin-top: 3.3rem;
		}
	}

	/* ---------- Grid list ---------- */
	&__list {
		display: grid;
		grid-template-columns:
			23.3rem
			28.2rem
			25.6rem
			37.3rem
			25.6rem
			28.2rem
			23.3rem;

		@media (max-width: 991.98px) {
			grid-template-columns:
				10.4rem
				12.6rem
				11.5rem
				16.3rem
				11.5rem
				12.6rem
				10.4rem;
		}
	}

	/* ---------- Item ---------- */
	&__item {
		width: 100%;
		flex-shrink: 0;
		position: relative;
		display: flex;
		align-items: center;
		justify-content: center;
		z-index: 1;
		grid-row: 1/2;

		/* центр — "телефон" */
		&[data-size='center'] {
			max-width: 36.2rem;
			padding: 1rem 1.36rem;
			grid-column: 4/5;

			@media (max-width: 991.98px) {
				padding: 0.61rem 0.5rem;
			}

			&::after {
				content: '';
				position: absolute;
				inset: 0;
				margin: auto;
				background: url('@/assets/img/viral-pranks/viral-pranks_phone.png') no-repeat
					center / cover;
				z-index: -1;
			}

			.viral-pranks__img {
				mask: url('@/assets/img/viral-pranks/viral-pranks_phone-mask.png') no-repeat
					center / contain;

				img {
					border-radius: 5rem;

					@media (max-width: 991.98px) {
						border-radius: 2rem;
					}
				}
			}
		}

		/* ---------- Sizes (лево) ---------- */
		&[data-size='large-left'] {
			max-width: 25.6rem;
			margin: 0 -0.4rem;
			z-index: 3;
			grid-column: 3/4;

			.viral-pranks {
				&__block {
					height: 69rem;

					@media (max-width: 991.98px) {
						height: 32rem;
					}
				}

				&__stat {
					&--likes {
						bottom: 16.3rem;
						left: 1.2rem;
						transform: rotate(13deg);

						@media (max-width: 991.98px) {
							bottom: 3rem;
							left: auto;
							right: -6rem;
						}
					}

					&--views {
						bottom: 10.7rem;
						left: -6.9rem;

						@media (max-width: 991.98px) {
							bottom: 0.5rem;
							left: auto;
							right: -2rem;
						}
					}

					&--views_tg {
					}
				}
			}

			transform: perspective(430px) rotateY(13deg);

			@media (max-width: 991.98px) {
				transform: perspective(225px) rotateY(13deg);
			}
		}

		&[data-size='medium-left'] {
			max-width: 28.2rem;
			margin: 0 -2.1rem;
			z-index: 2;
			grid-column: 2/3;

			@media (max-width: 991.98px) {
				margin: 0;
			}

			.viral-pranks {
				&__block {
					height: 66.6rem;

					@media (max-width: 991.98px) {
						height: 31.5rem;
					}
				}

				&__stat {
					&--likes {
						top: -1.5rem;
						left: -3.8rem;
					}

					&--views {
					}

					&--views_tg {
					}
				}
			}

			transform: perspective(430px) rotateY(13deg);

			@media (max-width: 991.98px) {
				transform: perspective(225px) rotateY(13deg);
			}
		}

		&[data-size='small-left'] {
			max-width: 23.3rem;
			margin: 0 -4.8rem;
			z-index: 1;
			grid-column: 1/2;

			@media (max-width: 991.98px) {
				margin: 0;
			}

			.viral-pranks {
				&__block {
					height: 62.6rem;

					@media (max-width: 991.98px) {
						height: 29.8rem;
					}
				}
			}

			transform: perspective(430px) rotateY(13deg);

			@media (max-width: 991.98px) {
				transform: perspective(225px) rotateY(13deg);
			}
		}

		/* ---------- Sizes (право) ---------- */
		&[data-size='large-right'] {
			max-width: 25.6rem;
			margin: 0 -0.6rem;
			z-index: 3;
			grid-column: 5/6;

			.viral-pranks {
				&__block {
					height: 69rem;

					@media (max-width: 991.98px) {
						height: 32rem;
					}
				}

				&__stat {
					&--likes {
					}

					&--views {
						top: -1.2rem;
						right: -2.4rem;

						@media (max-width: 991.98px) {
							top: -0.7rem;
							right: 5.6rem;
						}
					}

					&--views_tg {
						top: 5rem;
						right: 3rem;
						transform: rotate(16deg);

						@media (max-width: 991.98px) {
							top: 2.8rem;
							right: 8rem;
							transform: rotate(13deg);
						}
					}
				}
			}

			transform: perspective(430px) rotateY(-13deg);

			@media (max-width: 991.98px) {
				transform: perspective(225px) rotateY(-13deg);
			}
		}

		&[data-size='medium-right'] {
			max-width: 28.2rem;
			margin: 0 1.7rem;
			z-index: 2;
			grid-column: 6/7;

			@media (max-width: 991.98px) {
				margin: 0;
			}

			.viral-pranks {
				&__block {
					height: 66.6rem;

					@media (max-width: 991.98px) {
						height: 31.5rem;
					}
				}

				&__stat {
					&--likes {
						bottom: 4.1rem;
						right: -5rem;
					}

					&--views {
					}

					&--views_tg {
					}
				}
			}

			transform: perspective(430px) rotateY(-13deg);

			@media (max-width: 991.98px) {
				transform: perspective(225px) rotateY(-13deg);
			}
		}

		&[data-size='small-right'] {
			max-width: 23.3rem;
			margin: 0 5.3rem;
			z-index: 1;
			grid-column: 7/8;

			@media (max-width: 991.98px) {
				margin: 0;
			}

			.viral-pranks {
				&__block {
					height: 62.6rem;

					@media (max-width: 991.98px) {
						height: 29.8rem;
					}
				}
			}

			transform: perspective(430px) rotateY(-13deg);

			@media (max-width: 991.98px) {
				transform: perspective(225px) rotateY(-13deg);
			}
		}
	}

	/* ---------- Block wrapper ---------- */
	&__block {
		width: 100%;
		// height: 100%;
	}

	/* ---------- Image ---------- */
	&__img {
		position: relative;
		width: 100%;
		height: 100%;

		img {
			width: 100%;
			height: 100%;
			object-fit: cover;
			border-radius: 2.5rem;
			// box-shadow: 0 10px 10px 0 rgba(0, 0, 0, 0.25);
		}
	}

	/* ---------- Stats ---------- */
	&__stat {
		z-index: 10;
		position: absolute;
		font-weight: 400;
		font-size: 3.3rem;
		text-transform: lowercase;
		color: #000;
		border-radius: 8rem;
		padding: 1.1rem 1.8rem 1.4rem 1.2rem;
		display: flex;
		align-items: center;
		gap: 0.5rem;
		white-space: nowrap;

		@media (max-width: 991.98px) {
			font-size: 1.6rem;
			padding: 0.8rem 1.2rem 0.8rem;
			border-radius: 3.6rem;

			svg {
				max-width: 1.6rem;
				max-height: 1.6rem;
			}
		}

		&--views {
			box-shadow:
				inset 0 0 36px 0 #fff,
				inset 0 0 5px 0 #fff,
				0 4px 47px 0 #343b00;
			background: #e2ff04;

			@media (max-width: 991.98px) {
				box-shadow:
					inset 0 0 14px 0 #fff,
					inset 0 0 2px 0 #fff,
					0 2px 19px 0 #343b00;
			}
		}

		&--likes {
			box-shadow:
				inset 0 0 28px 0 #fff,
				inset 0 0 4px 0 #fff,
				0 3px 21px 0 rgba(52, 59, 0, 0.73);
			background: #4fcdee;

			@media (max-width: 991.98px) {
				box-shadow:
					inset 0 0 13px 0 #fff,
					inset 0 0 2px 0 #fff,
					0 1px 9px 0 rgba(52, 59, 0, 0.73);
			}
		}

		&--views_tg {
			box-shadow:
				inset 0 0 28px 0 #fff,
				inset 0 0 4px 0 #fff,
				0 3px 36px 0 #343b00;
			background: #ff7262;

			@media (max-width: 991.98px) {
				box-shadow:
					inset 0 0 13px 0 #fff,
					inset 0 0 2px 0 #fff,
					0 1px 16px 0 #343b00;
			}
		}
	}
}
</style>
